'use client';

import React, { useMemo } from 'react';
import { AppSidebar, type AppNavEntry } from './app-sidebar';
import { getFilteredNav, isPathActive, navigationOwnerPath } from '@/lib/nav';
import { prefetchRoute } from '@/lib/prefetch';
import { useWorkspaceStore } from '@/hooks/state';
import { useWorkspace } from '@/hooks/useWorkspace';
import { signOut } from '@/services/auth/authService';
import { isKnitKnotEmail } from '../../constants';
import { hostedWorkspaceUrl } from '../../lib/appUrls';
import { useLocation } from 'react-router-dom';
import { useEntitlements } from '@/hooks/api/useEntitlements';

// Cross-app links go through @knitknot/shared/lib/appUrls — never an inline
// env ternary with a hardcoded port. See that module for why.

const LOCKED_HINT = 'Available once setup is finished';

/**
 * The console's adapter over the canonical `AppSidebar`.
 *
 * Everything visual lives in `AppSidebar` (2026-08-17): this file is now only
 * the console's DATA — its nav table, its lock policy, its prefetcher, its
 * workspace-derived external href, and its sign-out. The internal staff app has
 * a sibling adapter over the same component, which is the point: one rail, two
 * route tables.
 */

interface SidebarProps {
  activeTab?: string;
  /** The workspace has not finished onboarding: every data destination renders
   *  greyed and inert, and the wizard becomes the one live page. Decided by the
   *  console (`lib/consoleLock`) and handed down — the sidebar renders what it
   *  is given. */
  locked?: boolean;
}

export const Sidebar: React.FC<SidebarProps> = ({ locked = false }) => {
  const location = useLocation();
  const { workspaceId, userInfo } = useWorkspaceStore();
  const { factLedgerBetaEnabled } = useEntitlements(workspaceId);
  const { activeWorkspace } = useWorkspace();
  // `[locked]`, not `[]` — an empty dep array would freeze the first value
  // forever and the sidebar would never un-grey when onboarding finished.
  const showInternal = isKnitKnotEmail(userInfo?.email);
  const filteredNav = useMemo(
    () => getFilteredNav(locked, showInternal, factLedgerBetaEnabled),
    [locked, showInternal, factLedgerBetaEnabled],
  );
  const hostedUrl = activeWorkspace?.slug ? hostedWorkspaceUrl(activeWorkspace.slug) : undefined;
  const activePath = navigationOwnerPath(location.pathname, location.search);

  // Resolve every external href HERE, so `AppSidebar` only ever renders
  // `item.href` and never has to know what a workspace is.
  const entries: AppNavEntry[] = useMemo(
    () =>
      filteredNav.map((entry) =>
        entry.isExternal
          ? { ...entry, href: entry.href.startsWith('http') ? entry.href : (hostedUrl ?? '#') }
          : entry,
      ),
    [filteredNav, hostedUrl],
  );

  return (
    <AppSidebar
      entries={entries}
      isActive={(href) => isPathActive(href, activePath)}
      onPrefetch={(href) => prefetchRoute(href, workspaceId ?? null)}
      onSignOut={signOut}
      lockedHint={LOCKED_HINT}
      forcedMode={locked ? 'collapsed' : undefined}
      defaultMode="hover"
      modeStorageKey="console-sidebar-mode"
    />
  );
};

export default Sidebar;
